import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Calendar, Plus, Loader2 } from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export const CeremoniesPage = () => {
  const [ceremonies, setCeremonies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [form, setForm] = useState({
    name: '',
    ceremony_type: '',
    date: '',
    time: '',
    venue: '',
    description: ''
  });

  useEffect(() => {
    fetchCeremonies();
  }, []);

  const fetchCeremonies = async () => {
    try {
      const { data } = await axios.get(`${API_URL}/api/ceremonies`, { withCredentials: true });
      setCeremonies(data);
    } catch (error) {
      toast.error('Error fetching ceremonies');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (submitting) return;
    setSubmitting(true);
    try {
      await axios.post(`${API_URL}/api/ceremonies`, form, { withCredentials: true });
      toast.success('Ceremony added!');
      setDialogOpen(false);
      setForm({ name: '', ceremony_type: '', date: '', time: '', venue: '', description: '' });
      fetchCeremonies();
    } catch (error) {
      const detail = error.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : 'Error adding ceremony');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-serif font-bold tracking-tight">Ceremonies</h1>
          <p className="text-muted-foreground mt-1">Plan all your wedding ceremonies</p>
        </div>
        <Dialog open={dialogOpen} onOpenChange={(open) => !submitting && setDialogOpen(open)}>
          <DialogTrigger asChild>
            <Button className="rounded-full" data-testid="add-ceremony-button">
              <Plus className="h-4 w-4 mr-2" />
              Add Ceremony
            </Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Add New Ceremony</DialogTitle>
              <DialogDescription>Create a new ceremony for your wedding</DialogDescription>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-2">
                <Label>Ceremony Name</Label>
                <Input
                  placeholder="e.g., Mehendi, Sangeet, Wedding"
                  value={form.name}
                  onChange={(e) => setForm({...form, name: e.target.value})}
                  required
                  disabled={submitting}
                  data-testid="ceremony-name-input"
                />
              </div>
              <div className="space-y-2">
                <Label>Type</Label>
                <Input
                  placeholder="e.g., Pre-wedding, Main Wedding"
                  value={form.ceremony_type}
                  onChange={(e) => setForm({...form, ceremony_type: e.target.value})}
                  required
                  disabled={submitting}
                  data-testid="ceremony-type-input"
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Date</Label>
                  <Input
                    type="date"
                    value={form.date}
                    onChange={(e) => setForm({...form, date: e.target.value})}
                    disabled={submitting}
                    data-testid="ceremony-date-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Time</Label>
                  <Input
                    type="time"
                    value={form.time}
                    onChange={(e) => setForm({...form, time: e.target.value})}
                    disabled={submitting}
                    data-testid="ceremony-time-input"
                  />
                </div>
              </div>
              <div className="space-y-2">
                <Label>Venue</Label>
                <Input
                  placeholder="Ceremony venue"
                  value={form.venue}
                  onChange={(e) => setForm({...form, venue: e.target.value})}
                  disabled={submitting}
                  data-testid="ceremony-venue-input"
                />
              </div>
              <div className="space-y-2">
                <Label>Description</Label>
                <Textarea
                  placeholder="Additional details..."
                  value={form.description}
                  onChange={(e) => setForm({...form, description: e.target.value})}
                  disabled={submitting}
                  data-testid="ceremony-description-input"
                />
              </div>
              <Button
                type="submit"
                className="w-full rounded-full"
                disabled={submitting}
                data-testid="submit-ceremony-button"
              >
                {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Add Ceremony'}
              </Button>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {ceremonies.length === 0 ? (
        <Card className="shadow-sm">
          <CardContent className="flex flex-col items-center justify-center py-12">
            <Calendar className="h-12 w-12 text-muted-foreground mb-4" />
            <p className="text-muted-foreground text-center">No ceremonies yet. Add your first ceremony!</p>
          </CardContent>
        </Card>
      ) : (
        <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
          {ceremonies.map((ceremony) => (
            <Card key={ceremony.id} className="shadow-sm hover:shadow-md transition-shadow" data-testid="ceremony-card">
              <CardHeader>
                <CardTitle className="font-serif">{ceremony.name}</CardTitle>
                <CardDescription className="uppercase tracking-wider text-xs">{ceremony.ceremony_type}</CardDescription>
              </CardHeader>
              <CardContent className="space-y-2 text-sm">
                {ceremony.date && (
                  <div className="flex items-center gap-2">
                    <Calendar className="h-4 w-4 text-muted-foreground" />
                    <span>{new Date(ceremony.date).toLocaleDateString()} {ceremony.time && `at ${ceremony.time}`}</span>
                  </div>
                )}
                {ceremony.venue && (
                  <p className="text-muted-foreground">{ceremony.venue}</p>
                )}
                {ceremony.description && (
                  <p className="text-muted-foreground mt-2">{ceremony.description}</p>
                )}
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
};
